import { Injectable } from '@angular/core';
import { SynchronizationsService } from '@proxy/services';
import { firstValueFrom } from 'rxjs';
import { SyncConsts } from '../../AppConsts';
import { LocalDb } from '../local-db';
import { LocalDbConsts } from '../local-db-consts';
import { ProgrammingSync } from '../objects/programming-sync';

@Injectable({ providedIn: 'root' })
export class ProgrammingRepository {
  constructor(
    private readonly _db: LocalDb,
    private readonly _syncService: SynchronizationsService
  ) {}

  async refreshProgrammings(): Promise<void> {
    const table = this._db.programmings;

    let skip = 0;
    let total = 0;

    let programmings: ProgrammingSync[] = [];

    do {
      const result = await firstValueFrom(this._syncService.getProgrammings({
        skipCount: skip,
        maxResultCount: SyncConsts.PageSize
      }));

      total = result.pagedResult.totalCount;

      programmings = programmings.concat(
        result.pagedResult.items.map(dto => ({
          id: dto.id,
          effectiveHoursPerShift: dto.effectiveHoursPerShift,
          shiftId: dto.shiftId,
          machineId: dto.machineId,
          teamId: dto.teamId,
          periodStart: dto.periodStart,
          periodEnd: dto.periodEnd,
        }))
      );

      skip += SyncConsts.PageSize;

    } while (skip < total);

    await this._db.transaction(LocalDbConsts.localDbReadWriteTransaction, table, async () => {
      await table.clear();
      await table.bulkAdd(programmings);
    });
  }

  async getAll(): Promise<ProgrammingSync[]> {
    return await this._db.programmings.toArray();
  }

  async getAllByTeamIdAndShiftIdAndDate(teamId: number, shiftId: number, date: Date): Promise<ProgrammingSync[]> {
    return this._db.programmings
      .where('[teamId+shiftId]')
      .equals([teamId, shiftId])
      .filter(programming =>
        new Date(programming.periodStart) <= date &&
        new Date(programming.periodEnd) >= date
      )
      .toArray();
  }

  async getAllByTeamAndDate(teamId: number, date: Date): Promise<ProgrammingSync[]> {
    return this._db.programmings
      .where('teamId')
      .equals(teamId)
      .filter(programming =>
        new Date(programming.periodStart) <= date &&
        new Date(programming.periodEnd) >= date
      )
      .toArray();
  }

  async getByMachineAndTeamAndShiftAndDate(machineId: number, teamId: number, shiftId: number, date: Date): Promise<ProgrammingSync> {
    return this._db.programmings
      .where('[teamId+shiftId+machineId]')
      .equals([teamId, shiftId, machineId])
      .filter(programming =>
        new Date(programming.periodStart) <= date &&
        new Date(programming.periodEnd) >= date
      )
      .first();
  }
}
